import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Btn, Modal, Field, Input, Select, StatusPill } from "@/components/ui-kit";
import { formatDate } from "@/lib/format";
import { Plus, Trash2 } from "lucide-react";

const empty = { code: "", discount_type: "percentage", discount_value: 10, usage_limit: 100, used_count: 0, expiry_date: "", is_active: true };

export default function AdminCoupons() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => setItems((await api.get("/admin/coupons")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    try {
      const data = { ...editing, discount_value: Number(editing.discount_value), usage_limit: Number(editing.usage_limit), used_count: Number(editing.used_count || 0) };
      if (!data.expiry_date) delete data.expiry_date;
      if (data.id) await api.patch(`/admin/coupons/${data.id}`, data);
      else await api.post("/admin/coupons", data);
      setOpen(false); setEditing(null); load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed to save coupon");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete coupon?")) return;
    await api.delete(`/admin/coupons/${id}`); load();
  };

  return (
    <div data-testid="admin-coupons-page">
      <PageHeader
        title="Coupons"
        subtitle="Promotional discount codes applied at subscription checkout."
        actions={<Btn data-testid="coupon-add" onClick={() => { setEditing({...empty}); setOpen(true); }}><Plus className="h-4 w-4" /> Add Coupon</Btn>}
      />
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <table className="tbl">
          <thead><tr><th>Code</th><th>Type</th><th>Value</th><th>Usage</th><th>Expiry</th><th>Status</th><th className="text-right"></th></tr></thead>
          <tbody>
            {items.length === 0 ? <tr><td colSpan={7} className="text-center py-10 text-zinc-500">No coupons yet.</td></tr> :
              items.map((c) => (
                <tr key={c.id}>
                  <td className="font-mono font-bold text-asphalt">{c.code}</td>
                  <td className="capitalize">{c.discount_type}</td>
                  <td>{c.discount_type === "percentage" ? `${c.discount_value}%` : `₹${c.discount_value}`}</td>
                  <td>{c.used_count} / {c.usage_limit}</td>
                  <td>{formatDate(c.expiry_date)}</td>
                  <td><StatusPill status={c.is_active ? "active" : "suspended"} /></td>
                  <td className="text-right">
                    <Btn variant="ghost" data-testid={`coupon-delete-${c.id}`} onClick={() => del(c.id)}><Trash2 className="h-4 w-4 text-red-600" /></Btn>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing?.id ? "Edit coupon" : "New coupon"} testid="coupon-modal"
        footer={<>
          <Btn variant="outline" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn data-testid="coupon-save" onClick={submit}>Save</Btn>
        </>}>
        {editing && (
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <Field label="Code" required><Input value={editing.code} onChange={(e) => setEditing({...editing, code: e.target.value.toUpperCase()})} required data-testid="coupon-code" /></Field>
            <Field label="Type" required>
              <Select value={editing.discount_type} onChange={(e) => setEditing({...editing, discount_type: e.target.value})}>
                <option value="percentage">Percentage</option>
                <option value="fixed">Fixed (INR)</option>
              </Select>
            </Field>
            <Field label="Value" required><Input type="number" value={editing.discount_value} onChange={(e) => setEditing({...editing, discount_value: e.target.value})} required /></Field>
            <Field label="Usage limit"><Input type="number" value={editing.usage_limit} onChange={(e) => setEditing({...editing, usage_limit: e.target.value})} /></Field>
            <Field label="Expiry date"><Input type="date" value={editing.expiry_date?.slice(0,10) || ""} onChange={(e) => setEditing({...editing, expiry_date: e.target.value})} /></Field>
            <Field label="Active"><label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({...editing, is_active: e.target.checked})} /> Active</label></Field>
            {err && <div className="col-span-2 text-sm text-red-600">{err}</div>}
          </form>
        )}
      </Modal>
    </div>
  );
}
